/**
 * Fortigate Exporter Profile API Service
 *
 * Provides CRUD operations for Fortigate exporter profiles and probes.
 */

import api from "./api";

const sp = (schemaName) => (schemaName ? { client: schemaName } : {});

// =============================================================================
// FortigateExporterProfile CRUD
// =============================================================================

/**
 * Get all Fortigate exporter profiles
 * @returns {Promise<Array>} List of FortigateExporterProfile objects
 */
export const getAllFortigateProfiles = async (schemaName = null) => {
  try {
    const response = await api.get("/fortigate-profiles/", { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching Fortigate profiles:", error);
    throw error;
  }
};

/**
 * Get all Fortigate exporter profiles with pagination
 * @param {Object} params - Query parameters (page, search, ordering, etc.)
 * @returns {Promise<Object>} Paginated response
 */
export const getFortigateProfilesWithPagination = async (params = {}, schemaName = null) => {
  try {
    const response = await api.get("/fortigate-profiles/", { params: { ...params, ...sp(schemaName) } });
    return response.data;
  } catch (error) {
    console.error("Error fetching Fortigate profiles:", error);
    throw error;
  }
};

/**
 * Get a single Fortigate exporter profile by ID
 * @param {number} id - FortigateExporterProfile ID
 * @returns {Promise<Object>} FortigateExporterProfile object with nested probes
 */
export const getFortigateProfileById = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/fortigate-profiles/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching Fortigate profile:", error);
    throw error;
  }
};

/**
 * Create a new Fortigate exporter profile
 * @param {Object} data - FortigateExporterProfile data (name, description, org)
 * @returns {Promise<Object>} Created FortigateExporterProfile object
 */
export const createFortigateProfile = async (data, schemaName = null) => {
  try {
    const response = await api.post("/fortigate-profiles/", data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error creating Fortigate profile:", error);
    throw error;
  }
};

/**
 * Update an existing Fortigate exporter profile
 * @param {number} id - FortigateExporterProfile ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated FortigateExporterProfile object
 */
export const updateFortigateProfile = async (id, data, schemaName = null) => {
  try {
    const response = await api.put(`/fortigate-profiles/${id}/`, data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error updating Fortigate profile:", error);
    throw error;
  }
};

/**
 * Delete a Fortigate exporter profile
 * @param {number} id - FortigateExporterProfile ID
 * @returns {Promise<Object>} Response message
 */
export const deleteFortigateProfile = async (id, schemaName = null) => {
  try {
    const response = await api.delete(`/fortigate-profiles/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error deleting Fortigate profile:", error);
    throw error;
  }
};

/**
 * Generate Fortigate profile YAML files
 * @returns {Promise<Object>} Response with file paths and profiles count
 */
export const generateFortigateProfiles = async (schemaName = null) => {
  try {
    const response = await api.post("/fortigate-profiles/generate_profiles/", {}, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error generating Fortigate profiles:", error);
    throw error;
  }
};

/**
 * Import Fortigate profiles from a YAML file
 * @param {File} file - The YAML file to import
 * @param {Boolean} makeGlobal - Whether to create global profiles (default: false)
 * @returns {Promise<Object>} Response with created/updated counts
 */
export const importFortigateProfilesFromYAML = async (file, makeGlobal = false, schemaName = null) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    // Add global query parameter if requested
    const params = { ...(makeGlobal ? { global: 'true' } : {}), ...sp(schemaName) };

    const response = await api.post("/fortigate-profiles/import_yaml/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: params,
    });
    return response.data;
  } catch (error) {
    console.error("Error importing Fortigate profiles from YAML:", error);
    throw error;
  }
};

/**
 * Get list of common Fortigate probes
 * @returns {Promise<Array>} List of common probe paths
 */
export const getCommonProbes = async (schemaName = null) => {
  try {
    const response = await api.get("/fortigate-profiles/common_probes/", { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching common probes:", error);
    throw error;
  }
};

// =============================================================================
// FortigateProbe CRUD
// =============================================================================

/**
 * Get all probes for a Fortigate profile
 * @param {number} profileId - Parent FortigateExporterProfile ID
 * @returns {Promise<Array>} List of FortigateProbe objects
 */
export const getFortigateProbesByProfile = async (profileId, schemaName = null) => {
  try {
    const response = await api.get(`/fortigate-probes/`, { params: { profile: profileId, ...sp(schemaName) } });
    return response.data;
  } catch (error) {
    console.error("Error fetching Fortigate probes:", error);
    throw error;
  }
};

/**
 * Get a single Fortigate probe by ID
 * @param {number} id - FortigateProbe ID
 * @returns {Promise<Object>} FortigateProbe object
 */
export const getFortigateProbeById = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/fortigate-probes/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching Fortigate probe:", error);
    throw error;
  }
};

/**
 * Create a new Fortigate probe
 * @param {Object} data - FortigateProbe data (profile, probe_path, probe_type)
 * @returns {Promise<Object>} Created FortigateProbe object
 */
export const createFortigateProbe = async (data, schemaName = null) => {
  try {
    const response = await api.post("/fortigate-probes/", data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error creating Fortigate probe:", error);
    throw error;
  }
};

/**
 * Update an existing Fortigate probe
 * @param {number} id - FortigateProbe ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated FortigateProbe object
 */
export const updateFortigateProbe = async (id, data, schemaName = null) => {
  try {
    const response = await api.put(`/fortigate-probes/${id}/`, data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error updating Fortigate probe:", error);
    throw error;
  }
};

/**
 * Delete a Fortigate probe
 * @param {number} id - FortigateProbe ID
 * @returns {Promise<Object>} Response message
 */
export const deleteFortigateProbe = async (id, schemaName = null) => {
  try {
    const response = await api.delete(`/fortigate-probes/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error deleting Fortigate probe:", error);
    throw error;
  }
};

// =============================================================================
// Default Export
// =============================================================================

export default {
  // FortigateExporterProfile
  getAllFortigateProfiles,
  getFortigateProfilesWithPagination,
  getFortigateProfileById,
  createFortigateProfile,
  updateFortigateProfile,
  deleteFortigateProfile,
  generateFortigateProfiles,
  importFortigateProfilesFromYAML,
  getCommonProbes,
  // FortigateProbe
  getFortigateProbesByProfile,
  getFortigateProbeById,
  createFortigateProbe,
  updateFortigateProbe,
  deleteFortigateProbe,
};
